// CRM main page — list/grid/table view of all contacts with search, filters,
// summary stats, CSV export, seed actions. Edit modal and Preview modal live
// in dedicated sub-components under ./crm/.
import React, { useEffect, useMemo, useState, useCallback } from "react";
import AppLayout from "../components/AppLayout";
import { api, getToken } from "../lib/api";
import {
  Plus, Search, Trash2, Sparkles, RefreshCw, Download,
  LayoutGrid, List as ListIcon, MessageCircle, Mail, Phone, Film,
  Building2, Pencil, MapPin, Languages as LangIcon, Clock, Users,
} from "lucide-react";
import { toast } from "sonner";

import {
  TYPES, STATUSES, EMPTY_FORM,
  STATUS_LABEL, STATUS_COLORS, TYPE_COLORS, TYPE_HEX, STATUS_HEX, typeLabel,
  getRecordStatuses, primaryStatus, matchesStatusFilter,
} from "./crm/constants";
import { calcAge, formatRelative } from "./crm/helpers";
import CRMForm from "./crm/CRMForm";
import CRMPreview from "./crm/CRMPreview";
import StudioProfile from "./crm/StudioProfile";
import MergeDuplicatesModal from "./crm/MergeDuplicatesModal";
import CrewCleanupPreviewModal from "./crm/CrewCleanupPreviewModal";
import CrewDirectory from "./crm/CrewDirectory";
import GroupChatLogModal from "./crm/GroupChatLogModal";
import { validateRecord } from "./crm/typeFields";
import { canExportCrewVCard } from "./crm/crewVCard";
import { useAuth } from "../context/AuthContext";

export default function CRM({ scope = "all", title, overline, scopeTypes = [] }) {
  const auth = useAuth();
  const canExportVCard = canExportCrewVCard(scope, auth?.user, getToken());
  const isStudioRecord = (p) => scope === "studios" || (p?.type || "").toLowerCase() === "studio";
  const [crewLoading, setCrewLoading] = useState(scope === "crew");
  const [crewLoadError, setCrewLoadError] = useState(null);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState([]);
  const [statusFilterMode, setStatusFilterMode] = useState("any");
  const [editing, setEditing] = useState(null);
  const [previewing, setPreviewing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [view, setView] = useState(() => localStorage.getItem("crm_view") || "grid");
  const [mergeOpen, setMergeOpen] = useState(false);
  const [groupLogOpen, setGroupLogOpen] = useState(false);

  const setViewPersist = (v) => { setView(v); localStorage.setItem("crm_view", v); };

  const load = useCallback(async () => {
    if (scope === "crew") {
      setCrewLoading(true);
      setCrewLoadError(null);
    }
    try {
      const [l, s] = await Promise.all([
        api.get(`/api/crm/?limit=${scope === "crew" ? 1000 : 500}${scopeTypes.length ? `&types=${encodeURIComponent(scopeTypes.join(","))}` : ""}`),
        api.get("/api/crm/summary")
      ]);
      setItems(l.data);
      setSummary(s.data);
    } catch (error) {
      if (scope !== "crew") throw error;
      setCrewLoadError(error?.response?.data?.detail || error?.message || "Could not load crew directory. Please try again.");
    } finally {
      if (scope === "crew") setCrewLoading(false);
    }
  }, [scope, scopeTypes]);

  useEffect(() => { load(); }, [load]);

  // AUTO-REFRESH: Listen for data changes and refetch
  useEffect(() => {
    const handleDataChange = (e) => {
      const { resourceType } = e.detail;
      if (resourceType === 'crm') {
        // Small delay to ensure backend has committed changes
        setTimeout(load, 100);
      }
    };
    
    window.addEventListener('gio:data-changed', handleDataChange);
    return () => window.removeEventListener('gio:data-changed', handleDataChange);
  }, [load]);

  const seed = async (force = false) => {
    try {
      const r = await api.post(`/api/crm/${force ? "seed-force" : "seed"}`);
      const merged = r.data.merged ?? 0;
      const inserted = r.data.inserted ?? 0;
      const kept = r.data.custom_kept ?? 0;
      if (force) {
        toast.success(`Safe top-up · ${inserted} added · ${merged} merged · ${kept} kept untouched`);
      } else {
        toast.success(`Seeded ${r.data.seeded} records`);
      }
      load();
    } catch (e) {
      if (e?.response?.status === 409) {
        if (window.confirm(
          e.response.data.detail +
          "\n\nProceed with SAFE top-up?\n" +
          "• Your photos, follow-ups & linked videos/studios will be preserved\n" +
          "• Custom records you added will stay untouched\n" +
          "• Only missing seed entries get added"
        )) seed(true);
      } else toast.error(e?.response?.data?.detail || "Seed failed");
    }
  };

  const openNew = () => { setEditing("new"); setForm(EMPTY_FORM); };
  const openEdit = (p) => { setEditing(p.id); setForm({ ...EMPTY_FORM, ...p }); setPreviewing(null); };
  const openPreview = (p) => { setPreviewing(p); };
  const close = () => { setEditing(null); setForm(EMPTY_FORM); };
  const closePreview = () => setPreviewing(null);

  const save = async () => {
    const invalid = validateRecord(form);
    if (invalid) return toast.error(invalid);
    try {
      if (editing === "new") {
        await api.post("/api/crm/", form);
        toast.success("Added");
      } else {
        await api.put(`/api/crm/${editing}`, form);
        toast.success("Saved");
      }
      close(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    await api.delete(`/api/crm/${id}`);
    load();
  };

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return items.filter((p) => {
      if (typeFilter !== "all" && p.type !== typeFilter) return false;
      if (!matchesStatusFilter(p, statusFilter, statusFilterMode)) return false;
      if (!q) return true;
      return (p.stage_name + " " + p.legal_name + " " + p.email + " " + p.twitter + " " + p.instagram + " " + p.country + " " + p.city + " " + (p.tags || []).join(" ")).toLowerCase().includes(q);
    });
  }, [items, query, typeFilter, statusFilter, statusFilterMode]);

  const viewTitle = title || (scope === "all" ? "Master CRM" : "CRM directory");
  const viewOverline = overline || (scope === "all" ? "ALL RECORDS · SHARED CRM SOURCE" : "FILTERED CRM SUBVIEW · SHARED SOURCE");

  if (scope === "crew") {
    return <CrewDirectory
      items={items}
      onRefresh={load}
      loading={crewLoading}
      loadError={crewLoadError}
      onUpdated={(updated) => setItems((current) => current.map((item) => item.id === updated.id ? updated : item))}
      title={viewTitle}
      overline={viewOverline}
      scopeTypes={scopeTypes}
      canExportVCard={canExportVCard}
    />;
  }

  return (
    <AppLayout title={viewTitle} overline={viewOverline}>
      {/* Summary */}
      <section className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8 fade-up" data-testid="crm-summary">
        <Stat label="Total" value={summary?.total ?? 0} testid="crm-total" />
        <Stat label="Models" value={summary?.by_type?.Model ?? 0} testid="crm-actors" />
        <Stat label="Brands" value={summary?.by_type?.Brand ?? 0} testid="crm-brands" />
        <Stat label="New Leads" value={summary?.by_status?.new_lead ?? 0} accent testid="crm-newleads" />
        <Stat label="Shoot Done" value={summary?.by_status?.["1st_shoot_completed"] ?? 0} testid="crm-done" />
      </section>

      {/* Toolbar */}
      <div className="flex items-center gap-3 mb-4 flex-wrap" data-testid="crm-toolbar">
        <div className="flex items-center gap-2 border border-zinc-900 bg-zinc-950 px-3 py-2 flex-1 min-w-[240px]">
          <Search size={14} className="text-zinc-600" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, handle, country, city..."
            data-testid="crm-search"
            className="bg-transparent text-white text-sm focus:outline-none flex-1" />
        </div>
        <Dropdown value={typeFilter} onChange={setTypeFilter} options={[{v:"all",l:"All types"}, ...TYPES.map(t => ({v:t,l:t}))]} testid="crm-type-filter" />
        <StatusFilter value={statusFilter} onChange={setStatusFilter} mode={statusFilterMode} onModeChange={setStatusFilterMode} />
        <div className="flex border rounded overflow-hidden" style={{ borderColor: "var(--gio-border)" }} data-testid="crm-view-toggle">
          <button onClick={() => setViewPersist("grid")} data-testid="crm-view-grid" title="Grid view"
            className="px-3 py-2 transition" style={{
              background: view === "grid" ? "var(--gio-primary-soft)" : "var(--gio-surface)",
              color: view === "grid" ? "var(--gio-primary)" : "var(--gio-muted)",
            }}>
            <LayoutGrid size={15} />
          </button>
          <button onClick={() => setViewPersist("table")} data-testid="crm-view-table" title="Table view"
            className="px-3 py-2 transition" style={{
              background: view === "table" ? "var(--gio-primary-soft)" : "var(--gio-surface)",
              color: view === "table" ? "var(--gio-primary)" : "var(--gio-muted)",
              borderLeft: "1px solid var(--gio-border)",
            }}>
            <ListIcon size={15} />
          </button>
        </div>
        <button onClick={openNew} data-testid="crm-add-button" className="btn-primary inline-flex items-center gap-2"><Plus size={14} /> Add</button>
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/exports/crm.csv`} target="_blank" rel="noreferrer" download
          onClick={(e) => {
            e.preventDefault();
            const token = localStorage.getItem("gio_token");
            fetch(`${process.env.REACT_APP_BACKEND_URL}/api/exports/crm.csv`, { headers: { Authorization: `Bearer ${token}` } })
              .then(r => r.blob()).then(blob => {
                const u = URL.createObjectURL(blob); const a = document.createElement("a");
                a.href = u; a.download = "gioagent_crm.csv"; a.click(); URL.revokeObjectURL(u);
              });
          }}
          data-testid="crm-export-button" className="btn-ghost inline-flex items-center gap-2"><Download size={14} /> CSV</a>
        <button onClick={() => seed(false)} data-testid="crm-seed-button" className="btn-ghost inline-flex items-center gap-2"><Sparkles size={14} /> Seed</button>
        <button onClick={() => seed(true)} data-testid="crm-reseed-button" className="btn-ghost inline-flex items-center gap-2" title="Safe top-up: adds missing seed entries, keeps your photos/follow-ups/edits"><RefreshCw size={14} /></button>
        <button onClick={() => setMergeOpen(true)} data-testid="crm-merge-button" className="btn-ghost inline-flex items-center gap-2" title={scope === "crew" ? "Review a read-only crew duplicate preview" : "Find & merge duplicate CRM contacts"}><Users size={14} /> {scope === "crew" ? "Review cleanup preview" : "Merge duplicates"}</button>
        <button onClick={() => setGroupLogOpen(true)} data-testid="crm-group-log-button" className="btn-ghost inline-flex items-center gap-2" title="Log a group chat / meeting interaction on multiple contacts at once"><MessageCircle size={14} /> Group log</button>
      </div>

      {/* Grid view (cards) */}
      {view === "grid" && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-5" data-testid="crm-grid">
          {filtered.length === 0 && (
            <div className="col-span-full text-center py-12" style={{ color: "var(--gio-muted-3)" }}>
              {items.length === 0 ? "CRM empty. Click 'Seed' to load 60+ records." : "No matches."}
            </div>
          )}
          {filtered.map((p) => (
            <CRMGridCard key={p.id} p={p} onPreview={openPreview} onEdit={openEdit} onDelete={remove} />
          ))}
        </div>
      )}

      {/* Table view */}
      {view === "table" && (
      <div className="border border-zinc-900 bg-zinc-950 overflow-x-auto" data-testid="crm-table">
        <table className="min-w-[1100px] w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-900 bg-black sticky top-0">
              <th className="p-3 overline text-left">Name</th>
              <th className="p-3 overline text-left">Type</th>
              <th className="p-3 overline text-left">Status</th>
              <th className="p-3 overline text-left">Country · City</th>
              <th className="p-3 overline text-left">Contact</th>
              <th className="p-3 overline text-left">Socials</th>
              <th className="p-3 overline text-left">Position</th>
              <th className="p-3 overline"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="p-12 text-center text-zinc-600">
                {items.length === 0 ? "CRM empty. Click 'Seed' to load 60+ records." : "No matches."}
              </td></tr>
            )}
            {filtered.map((p) => (
              <tr key={p.id} onClick={() => openPreview(p)} data-testid={`crm-row-${p.id}`}
                className="border-b border-zinc-900/50 hover:bg-zinc-900/30 cursor-pointer align-top">
                <td className="p-3">
                  <div className="flex items-center gap-3">
                    {p.photos && p.photos.length > 0 ? (
                      <div className="relative">
                        <img src={p.photos[0]} alt="" className="w-14 h-14 rounded-lg object-cover border" style={{ borderColor: "var(--gio-border)" }} />
                        {p.photos.length > 1 && (
                          <span className="absolute -bottom-1 -right-1 text-[9px] font-bold px-1 rounded"
                            style={{ background: "rgba(0,0,0,0.85)", color: "#fff" }}>
                            +{p.photos.length - 1}
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="w-14 h-14 rounded-lg flex items-center justify-center font-bold text-[16px]"
                           style={{ background: "var(--gio-primary-soft)", color: "var(--gio-primary)", border: "1px dashed var(--gio-border)" }}
                           title="No photo — open profile to upload">
                        {(p.stage_name || "?").slice(0, 2).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <div className="font-semibold" style={{ color: "var(--gio-text)" }}>{p.stage_name}</div>
                      {p.legal_name && p.legal_name !== p.stage_name && <div className="text-[11px] mt-0.5" style={{ color: "var(--gio-muted-2)" }}>{p.legal_name}</div>}
                    </div>
                  </div>
                </td>
                <td className="p-3">
                  <span className={`text-[10px] font-mono-ui uppercase tracking-wider border px-2 py-0.5 ${TYPE_COLORS[p.type] || TYPE_COLORS.Other}`}>{typeLabel(p.type)}</span>
                </td>
                <td className="p-3">
                  <div className="flex flex-wrap gap-1.5 min-w-[150px]">
                    {getRecordStatuses(p).map((status) => {
                      const color = STATUS_HEX[status] || "#94a3b8";
                      return <span key={status} className="text-[9px] font-bold uppercase tracking-wide border rounded-full px-2 py-1 whitespace-nowrap" style={{ color, borderColor: `${color}66`, background: `${color}18` }}>
                        {STATUS_LABEL[status] || status.replace(/_/g, " ")}
                      </span>;
                    })}
                  </div>
                </td>
                <td className="p-3 text-zinc-400">
                  <div>{p.country || <span className="text-zinc-700">—</span>}</div>
                  {p.city && <div className="text-zinc-500 text-[11px]">{p.city}</div>}
                </td>
                <td className="p-3 text-zinc-400">
                  {p.email && <div className="text-[11px] break-all">{p.email}</div>}
                  {p.phone && <div className="text-[11px]">{p.phone}</div>}
                  {!p.email && !p.phone && <span className="text-zinc-700">—</span>}
                </td>
                <td className="p-3 text-zinc-400 text-[11px]">
                  {p.twitter && (
                    <a href={`https://x.com/${p.twitter.replace(/^@/, "")}`} target="_blank" rel="noreferrer"
                       onClick={(e) => e.stopPropagation()}
                       data-testid={`crm-row-twitter-${p.id}`}
                       className="hover:underline" style={{ color: "var(--gio-primary)" }}>
                      𝕏 @{p.twitter.replace(/^@/, "")}
                    </a>
                  )}
                  {p.instagram && (
                    <a href={`https://instagram.com/${p.instagram.replace(/^@/, "")}`} target="_blank" rel="noreferrer"
                       onClick={(e) => e.stopPropagation()}
                       data-testid={`crm-row-ig-${p.id}`}
                       className="hover:underline block" style={{ color: "var(--gio-secondary)" }}>
                      IG: {p.instagram.replace(/^@/, "")}
                    </a>
                  )}
                  {!p.twitter && !p.instagram && <span className="text-zinc-700">—</span>}
                </td>
                <td className="p-3 text-zinc-400">{p.position || <span className="text-zinc-700">—</span>}</td>
                <td className="p-3 text-right">
                  <div className="inline-flex items-center gap-2">
                    <button onClick={(e) => { e.stopPropagation(); openEdit(p); }} data-testid={`crm-row-edit-${p.id}`}
                      title="Edit"
                      className="text-zinc-600 hover:text-[color:var(--gio-secondary)]">
                      <Pencil size={13} />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); remove(p.id); }} data-testid={`crm-del-${p.id}`} className="text-zinc-600 hover:text-[color:var(--gio-primary)]">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {/* Preview modal — Studio records get the dedicated partnership profile */}
      {previewing && isStudioRecord(previewing) && (
        <StudioProfile
          person={previewing}
          onClose={closePreview}
          onEdit={() => { openEdit(previewing); }}
          onDelete={async () => { await remove(previewing.id); closePreview(); }}
          onLocalUpdate={(updated) => {
            setPreviewing(updated);
            setItems((prev) => prev.map(it => it.id === updated.id ? updated : it));
          }}
        />
      )}
      {previewing && !isStudioRecord(previewing) && (
        <CRMPreview
          person={previewing}
          onClose={closePreview}
          onEdit={() => { openEdit(previewing); }}
          onDelete={async () => { await remove(previewing.id); closePreview(); }}
          onLocalUpdate={(updated) => {
            setPreviewing(updated);
            setItems((prev) => prev.map(it => it.id === updated.id ? updated : it));
          }}
        />
      )}

      {/* Edit/New modal */}
      {editing && (
        <CRMForm editing={editing} form={form} setForm={setForm} onClose={close} onSave={save} />
      )}

      {/* Merge duplicates modal */}
      {mergeOpen && (
        scope === "crew" ? (
          <CrewCleanupPreviewModal onClose={() => setMergeOpen(false)} />
        ) : (
          <MergeDuplicatesModal
            onClose={() => setMergeOpen(false)}
            onMerged={() => { setMergeOpen(false); load(); }}
          />
        )
      )}

      {/* Group chat log modal */}
      {groupLogOpen && (
        <GroupChatLogModal
          contacts={items}
          onClose={() => setGroupLogOpen(false)}
          onSaved={() => { setGroupLogOpen(false); load(); }}
        />
      )}

      <style>{`
        .gf-input { width: 100%; background: #000; border: 1px solid #27272a; color: white; padding: 10px 12px; font-size: 13px; outline: none; font-family: 'Manrope', sans-serif; transition: border-color 0.2s; }
        .gf-input:focus { border-color: var(--gio-primary); }
      `}</style>
    </AppLayout>
  );
}

// ---------- Tiny subcomponents kept inline (used only here) ----------

function Stat({ label, value, testid, accent }) {
  return (
    <div className={`border bg-zinc-950 p-4 ${accent ? "border-[color:var(--gio-primary)]" : "border-zinc-900"}`} data-testid={testid}>
      <div className="overline">{label}</div>
      <div className={`font-heading text-3xl mt-1 ${accent ? "text-[color:var(--gio-primary)]" : "text-white"}`}>{value}</div>
    </div>
  );
}

function Dropdown({ value, onChange, options, testid }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="bg-zinc-950 border border-zinc-900 text-white text-xs font-mono-ui uppercase tracking-wider py-2 px-3 focus:outline-none focus:border-[color:var(--gio-primary)]">
      {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
    </select>
  );
}

function StatusFilter({ value, onChange, mode, onModeChange }) {
  const toggle = (status) => onChange(
    value.includes(status) ? value.filter((item) => item !== status) : [...value, status]
  );
  return (
    <details className="relative" data-testid="crm-status-filter">
      <summary className="list-none cursor-pointer select-none bg-zinc-950 border border-zinc-900 text-white text-xs font-mono-ui uppercase tracking-wider py-2 px-3 hover:border-[color:var(--gio-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gio-primary)]">
        Status{value.length ? ` · ${value.length}` : " · All"}
      </summary>
      <div className="absolute left-0 top-full z-40 mt-2 w-[min(90vw,34rem)] rounded-xl border p-3 shadow-xl" style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface)", boxShadow: "var(--gio-shadow-lg)" }}>
        <div className="flex items-center justify-between gap-3 mb-3">
          <label className="flex items-center gap-2 text-xs" style={{ color: "var(--gio-muted)" }}>
            Match
            <select value={mode} onChange={(event) => onModeChange(event.target.value)} aria-label="Status filter matching mode" className="gio-input py-1.5 px-2 text-xs">
              <option value="any">Any selected</option>
              <option value="all">All selected</option>
            </select>
          </label>
          <button type="button" onClick={() => onChange([])} disabled={!value.length} className="btn-ghost text-xs disabled:opacity-40">Clear</button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 max-h-72 overflow-y-auto pr-1">
          {STATUSES.map((status) => {
            const selected = value.includes(status);
            const color = STATUS_HEX[status] || "#94a3b8";
            return <button key={status} type="button" aria-pressed={selected} onClick={() => toggle(status)} className="text-left rounded-lg border px-2 py-1.5 text-[10px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gio-primary)]" style={{ borderColor: selected ? `${color}99` : "var(--gio-border)", background: selected ? `${color}20` : "var(--gio-surface-2)", color: selected ? color : "var(--gio-muted)" }}>
              {STATUS_LABEL[status] || status.replace(/_/g, " ")}
            </button>;
          })}
        </div>
      </div>
    </details>
  );
}

function CRMGridCard({ p, onPreview, onEdit, onDelete }) {
  const typeColor = TYPE_HEX[p.type] || "#94a3b8";
  const recordStatuses = getRecordStatuses(p);
  const primary = primaryStatus(recordStatuses);
  const statusColor = STATUS_HEX[primary] || "#94a3b8";
  const initials = (p.stage_name || "?").split(/\s+/).map(w => w[0]).join("").slice(0, 2).toUpperCase();
  const age = calcAge(p.date_of_birth);
  const photos = p.photos || [];
  const fuPending = (p.follow_ups || []).filter(f => !f.done).length;
  const lastFu = (p.follow_ups || []).slice().sort((a, b) => (b.date || "").localeCompare(a.date || ""))[0];
  const lastFuRel = lastFu ? formatRelative(lastFu.date) : null;
  const linkedV = p.linked_video_ids?.length || 0;
  const linkedS = p.linked_studio_ids?.length || 0;
  const hasContact = !!(p.email || p.phone);
  return (
    <div onClick={() => onPreview(p)} data-testid={`crm-card-${p.id}`}
      className="group relative cursor-pointer rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-2"
      style={{
        background: "linear-gradient(135deg, rgba(20,20,30,0.95) 0%, rgba(10,10,20,0.95) 100%)",
        border: "1px solid rgba(255,16,240,0.25)",
        boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
      }}>
      <div className="absolute inset-0 rounded-2xl pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{ 
          boxShadow: `0 0 0 2px ${typeColor}88, 0 0 40px rgba(255,16,240,0.4), 0 30px 60px -15px ${typeColor}66`,
          border: "1px solid rgba(255,16,240,0.5)"
        }} />

      <div className="relative aspect-[4/5] overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${typeColor}25 0%, ${typeColor}05 100%)` }}>
        {photos.length > 0 ? (
          <>
            <img src={photos[0]} alt={p.stage_name}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              loading="lazy" />
            <div className="absolute inset-0" style={{
              background: "linear-gradient(180deg, rgba(10,15,26,0) 35%, rgba(10,15,26,0.55) 70%, rgba(10,15,26,0.95) 100%)",
            }} />
            {photos.length > 1 && (
              <span className="absolute top-2.5 right-12 text-[10px] font-bold px-2 py-0.5 rounded-full backdrop-blur-md"
                style={{ background: "rgba(0,0,0,0.55)", color: "#fff", border: "1px solid rgba(255,255,255,0.15)" }}
                data-testid={`crm-card-photo-count-${p.id}`}>
                +{photos.length - 1}
              </span>
            )}
          </>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center">
            <div className="w-24 h-24 rounded-full flex items-center justify-center font-heading text-4xl shadow-2xl"
              style={{ background: `linear-gradient(135deg, ${typeColor} 0%, ${typeColor}aa 100%)`, color: "#fff" }}>
              {initials}
            </div>
          </div>
        )}

        <div className="absolute top-2.5 left-2.5 flex flex-wrap items-start gap-1.5 max-w-[75%]">
          <span className="text-[9.5px] font-bold uppercase tracking-wider px-2 py-1 rounded-full backdrop-blur-md w-fit"
            style={{ background: `${typeColor}E6`, color: "#fff" }}>
            {typeLabel(p.type)}
          </span>
          {recordStatuses.map((status) => {
            const color = STATUS_HEX[status] || statusColor;
            return <span key={status} className="text-[8px] leading-tight font-bold uppercase tracking-wide px-1.5 py-1 rounded-full backdrop-blur-md border max-w-full" style={{ background: "rgba(8,8,16,0.82)", color, borderColor: `${color}80` }}>
              {STATUS_LABEL[status] || status.replace(/_/g, " ")}
            </span>;
          })}
        </div>

        <div className="absolute top-2.5 right-2.5 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-all">
          <button onClick={(e) => { e.stopPropagation(); onEdit(p); }} data-testid={`crm-card-edit-${p.id}`}
            title="Edit"
            className="w-8 h-8 rounded-full flex items-center justify-center transition-all hover:scale-110"
            style={{ background: "rgba(20,184,166,0.92)", color: "#fff", backdropFilter: "blur(8px)" }}>
            <Pencil size={13} />
          </button>
          <button onClick={(e) => { e.stopPropagation(); onDelete(p.id); }} data-testid={`crm-card-del-${p.id}`}
            title="Delete"
            className="w-8 h-8 rounded-full flex items-center justify-center transition-all hover:scale-110"
            style={{ background: "rgba(220,38,38,0.92)", color: "#fff", backdropFilter: "blur(8px)" }}>
            <Trash2 size={13} />
          </button>
        </div>

        {fuPending > 0 && (
          <div className="absolute top-2.5 right-2.5 group-hover:opacity-0 transition-opacity"
            data-testid={`crm-card-pending-${p.id}`}>
            <span className="text-[10px] font-bold px-2 py-1 rounded-full inline-flex items-center gap-1 shadow-lg"
              style={{ background: "#f97316", color: "#fff", boxShadow: "0 0 0 3px rgba(249,115,22,0.25)" }}>
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> {fuPending} TODO
            </span>
          </div>
        )}

        <div className="absolute bottom-0 left-0 right-0 p-3.5 text-white">
          <div className="font-heading text-[18px] leading-tight truncate drop-shadow-2xl">
            {p.stage_name}
            {age && <span className="ml-1.5 text-[13px] opacity-80 font-mono-ui">· {age}</span>}
          </div>
          <div className="flex items-center gap-2 text-[11px] opacity-90 mt-0.5 truncate">
            {(p.country || p.city) && (
              <span className="inline-flex items-center gap-1 truncate">
                <MapPin size={10} className="shrink-0" />
                <span className="truncate">{[p.city, p.country].filter(Boolean).join(", ")}</span>
              </span>
            )}
            {p.languages && (
              <>
                {(p.country || p.city) && <span className="opacity-50">·</span>}
                <span className="inline-flex items-center gap-1 truncate min-w-0">
                  <LangIcon size={10} className="shrink-0" />
                  <span className="truncate">{p.languages.split(/[,;]/)[0].trim()}</span>
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="p-4 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          {p.position && (
            <span className="text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-lg bg-purple-900/20 text-purple-300 border border-purple-500/30">
              {p.position}
            </span>
          )}
          {p.body_type && (
            <span className="text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-lg truncate max-w-[120px] bg-indigo-900/20 text-indigo-300 border border-indigo-500/30"
              title={p.body_type}>
              {p.body_type}
            </span>
          )}
          {p.dick_size && (
            <span className="text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-lg bg-gradient-to-r from-pink-900/30 to-purple-900/30 text-pink-300 border border-pink-500/30">
              {p.dick_size}cm
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap text-xs">
          {(p.follow_ups?.length || 0) > 0 && (
            <span className="font-bold uppercase tracking-wider px-1.5 py-0.5 rounded inline-flex items-center gap-0.5"
              style={{ background: "rgba(6,182,212,0.15)", color: "#06B6D4" }}
              data-testid={`crm-card-fu-${p.id}`}>
              <MessageCircle size={9} /> {p.follow_ups.length}
            </span>
          )}
          {linkedV > 0 && (
            <span className="font-bold uppercase tracking-wider px-1.5 py-0.5 rounded inline-flex items-center gap-0.5"
              style={{ background: "rgba(20,184,166,0.15)", color: "#14B8A6" }}
              data-testid={`crm-card-lv-${p.id}`}>
              <Film size={9} /> {linkedV}
            </span>
          )}
          {linkedS > 0 && (
            <span className="font-bold uppercase tracking-wider px-1.5 py-0.5 rounded inline-flex items-center gap-0.5"
              style={{ background: "rgba(168,85,247,0.18)", color: "#a855f7" }}
              data-testid={`crm-card-st-${p.id}`}
              title={`${linkedS} studio collab(s)`}>
              <Building2 size={9} /> {linkedS}
            </span>
          )}
          {hasContact && (
            <span className="font-bold uppercase tracking-wider px-1.5 py-0.5 rounded inline-flex items-center gap-0.5"
              style={{ background: "rgba(34,197,94,0.12)", color: "#22c55e" }}
              title={[p.email, p.phone].filter(Boolean).join(" · ")}>
              {p.email && <Mail size={9} />}{p.phone && <Phone size={9} />}
            </span>
          )}
          {lastFuRel && (
            <span className="font-bold uppercase tracking-wider inline-flex items-center gap-0.5 ml-auto"
              style={{ color: "var(--gio-muted-3)" }}
              title={`Last activity: ${lastFu.note}`}>
              <Clock size={9} /> {lastFuRel}
            </span>
          )}
        </div>

        {(p.twitter || p.instagram || p.onlyfans) && (
          <div className="flex items-center gap-3 pt-1 border-t border-zinc-900/60 text-[12px]">
            {p.twitter && (
              <a href={`https://x.com/${p.twitter.replace(/^@/, "")}`} target="_blank" rel="noreferrer"
                onClick={(e) => e.stopPropagation()} data-testid={`crm-card-twitter-${p.id}`}
                className="inline-flex items-center gap-1 hover:underline truncate min-w-0"
                style={{ color: "var(--gio-text)" }}>
                <span style={{ color: "var(--gio-muted-2)" }}>𝕏</span>
                <span className="truncate text-[11px]">@{p.twitter.replace(/^@/, "")}</span>
              </a>
            )}
            {p.instagram && (
              <a href={`https://instagram.com/${p.instagram.replace(/^@/, "")}`} target="_blank" rel="noreferrer"
                onClick={(e) => e.stopPropagation()} data-testid={`crm-card-ig-${p.id}`}
                className="inline-flex items-center gap-1 hover:underline truncate"
                style={{ color: "#ec4899" }}>
                <span className="text-[11px]">IG</span>
              </a>
            )}
            {p.onlyfans && (
              <a href={`https://onlyfans.com/${p.onlyfans.replace(/^@/, "")}`} target="_blank" rel="noreferrer"
                onClick={(e) => e.stopPropagation()} data-testid={`crm-card-of-${p.id}`}
                className="hover:underline truncate text-[11px]" style={{ color: "#00AFF0" }}>
                OF
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

